import { useState } from "react";
import { useDrag } from "react-dnd";
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Checkbox,
  Chip,
  Avatar,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutlined,
  ChatBubbleOutline,
  AttachFile,
  Repeat,
  FavoriteBorder,
} from "@mui/icons-material";

export const priorityConfig = {
  high: { label: "High", color: "#EF4444", bg: "#FEE2E2" },
  medium: { label: "Medium", color: "#D97706", bg: "#FEF3C7" },
  low: { label: "Low", color: "#2563EB", bg: "#DBEAFE" },
};

export default function TaskCard({
  task,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onToggleComplete,
  isDragDisabled = false,
}) {
  const [isHovered, setIsHovered] = useState(false);

  // React DnD hook - disabled if isDragDisabled is true
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "TASK",
      canDrag: () => !isDragDisabled && task?.status !== "completed",
      item: { id: task?.id, status: task?.status },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [task?.id, task?.status, isDragDisabled]
  );

  if (!task || !task.id) {
    console.error("Invalid task provided to TaskCard:", task);
    return null;
  }

  // Overdue calculation
  const isOverdue = (() => {
    try {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      if (isNaN(dueDate.getTime())) return false;
      // Set to midnight for fair date comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDate < today && task.status !== "completed";
    } catch (error) {
      console.error("Error parsing due date:", error);
      return false;
    }
  })();

  const priority = priorityConfig[task.priority] || {
    label: task.priority || "Unknown",
    color: "#717182",
    bg: "#ECECF0",
  };

  const assigneeName = task.assignee?.name ?? "Unassigned";
  const assigneeAvatar = task.assignee?.avatar ?? "?";
  const assigneeImage = task.assignee?.image ?? null;
  const totalSubtasks = task.totalSubtasks ?? 0;
  const completedSubtasks = task.completedSubtasks ?? 0;
  const commentsCount = task.comments?.length ?? 0;
  const attachmentsCount = task.attachments?.length ?? 0;
  const likes = task.likes ?? 0;
  const taskTitle = task.title ?? "Untitled Task";

  return (
    <Card
      ref={drag}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onTaskClick(task.id)}
      elevation={0}
      sx={{
        width: "100%",
        mb: 1.5,
        cursor: isDragDisabled ? "default" : "pointer",
        opacity: isDragging ? 0.5 : task.status === "completed" ? 0.72 : 1,
        transform: isDragging ? "rotate(3deg)" : "none",
        border: "1px solid",
        borderColor: isHovered ? "#6C5CE7" : "rgba(0,0,0,0.1)",
        boxShadow: isDragging
          ? "0 20px 40px rgba(0,0,0,0.18)"
          : isHovered
          ? "0 4px 16px rgba(0,0,0,0.10)"
          : "0 1px 3px rgba(0,0,0,0.07)",
        transition:
          "box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
        borderRadius: "8px",
        bgcolor: "white",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" alignItems="flex-start" gap={1} mb={1}>
          <Checkbox
            checked={task.status === "completed"}
            disabled={isDragDisabled} // Viewer cannot toggle completion
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete(task.id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{
              p: 0,
              mt: 0.3,
              color: "#ABABAB",
              "&.Mui-checked": { color: "#6C5CE7" },
            }}
          />
          <Box flex={1} minWidth={0}>
            <Typography
              fontSize={13}
              fontWeight={500}
              mb={0.75}
              lineHeight={1.4}
              sx={{
                textDecoration:
                  task.status === "completed" ? "line-through" : "none",
                color: task.status === "completed" ? "#717182" : "#1a1a1a",
              }}
            >
              {taskTitle}
            </Typography>
            <Chip
              label={priority.label}
              size="small"
              sx={{
                bgcolor: priority.bg,
                color: priority.color,
                fontWeight: 500,
                fontSize: "11px",
                height: 20,
                borderRadius: "4px",
                "& .MuiChip-label": { px: "6px" },
              }}
            />
          </Box>
          {isHovered && !isDragDisabled && (
            <Stack
              direction="row"
              gap={0.25}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                sx={{ width: 24, height: 24, color: "#717182" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTask(task.id, e);
                }}
              >
                <EditOutlined sx={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ width: 24, height: 24, color: "#717182" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteTask(task.id, e);
                }}
              >
                <DeleteOutlined sx={{ fontSize: 13 }} />
              </IconButton>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <Avatar
            src={assigneeImage ? `https://mindsinaction.com.na/assets/media/pictures/${assigneeImage}` : undefined}
            sx={{
              width: 20,
              height: 20,
              fontSize: "10px",
              bgcolor: "#6C5CE7",
              color: "white",
            }}
          >
            {assigneeAvatar}
          </Avatar>
          <Typography variant="caption" color="#717182" noWrap>
            {assigneeName}
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          display="block"
          mb={totalSubtasks > 0 ? 1 : 1.5}
          color={isOverdue ? "#EF4444" : "#717182"}
          fontWeight={isOverdue ? 500 : 400}
        >
          {task.dueDate
            ? `Due: ${new Date(task.dueDate).toLocaleDateString()}`
            : "No due date"}
        </Typography>

        {totalSubtasks > 0 && (
          <Box mb={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              mb={0.5}
            >
              <Typography variant="caption" color="#717182">
                Subtasks
              </Typography>
              <Typography variant="caption" color="#717182">
                {completedSubtasks}/{totalSubtasks}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={
                totalSubtasks > 0
                  ? (completedSubtasks / totalSubtasks) * 100
                  : 0
              }
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: "#ECECF0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#6C5CE7",
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            gap={1.5}
            color="#717182"
            alignItems="center"
          >
            {commentsCount > 0 && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <ChatBubbleOutline sx={{ fontSize: 14 }} />
                <Typography variant="caption">{commentsCount}</Typography>
              </Stack>
            )}
            {attachmentsCount > 0 && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <AttachFile sx={{ fontSize: 14 }} />
                <Typography variant="caption">{attachmentsCount}</Typography>
              </Stack>
            )}
            {task.recurring && <Repeat sx={{ fontSize: 14 }} />}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            color="#717182"
          >
            <FavoriteBorder sx={{ fontSize: 14 }} />
            <Typography variant="caption">{likes}</Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
